import React, { useCallback, useEffect, useRef, useState } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import { Stack } from '@atlaskit/primitives/compiled';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import type { ActionKeys } from '@atlassian/conversation-assistant-message-actions-api/constants';
import type { JsmAtlassianProjectCreateJsmRequestTypeWithFieldsPayload } from '@atlassian/conversation-assistant-message-actions-api/jsm';
import type {
	ActionContentProcessorFn,
	ActionRendererFor,
} from '@atlassian/conversation-assistant-message-actions-api/types';
import {
	isActionExecutedSuccessfully,
	type AssistantMessage,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';

import { analyticsAttributes } from './constants';
import { CreateRequestTypeCard } from './CreateRequestTypeCard';

const actionResponseIndicatesSuccess: ActionContentProcessorFn<boolean> = (
	message: AssistantMessage,
) => isActionExecutedSuccessfully(message);

type JsmCreateRequestTypeActionProps = ActionRendererFor<
	typeof ActionKeys.JsmAtlassianProjectCreateJsmRequestTypeWithFields
>;

const ACTION_SUBJECT = 'jsmCreateRequestTypeCard';

export const JsmCreateRequestTypeAction = ({
	actions,
	getStatus,
	onCustomDataConfirm,
	conversationId,
	messageId,
	isHistoricalMessage,
}: JsmCreateRequestTypeActionProps): React.JSX.Element | null => {
	const { sendAnalyticsEvent } = useAnalytics();

	const [awaitingInvocationId, setAwaitingInvocationId] = useState<string | null>(null);
	const [succeededFromResponseByInvocationId, setSucceededFromResponseByInvocationId] = useState<
		Record<string, boolean>
	>({});

	const isFeatureGateEnabled = fg('rovo_chat_jsm_manage_request_type_anthropic');
	const viewedInvocationIdsRef = useRef<Set<string>>(new Set());

	useEffect(() => {
		if (!isFeatureGateEnabled || isHistoricalMessage || actions.length === 0) {
			return;
		}

		actions.forEach((action) => {
			if (viewedInvocationIdsRef.current.has(action.invocationId)) {
				return;
			}
			viewedInvocationIdsRef.current.add(action.invocationId);

			sendAnalyticsEvent({
				eventType: 'ui',
				action: 'viewed',
				actionSubject: ACTION_SUBJECT,
				actionSubjectId: ACTION_SUBJECT,
				attributes: {
					...analyticsAttributes,
					conversationId,
					messageId,
				},
			});
		});
	}, [
		actions,
		conversationId,
		messageId,
		isHistoricalMessage,
		isFeatureGateEnabled,
		sendAnalyticsEvent,
	]);

	const handleNextForAction = useCallback(
		async (action: (typeof actions)[number]) => {
			sendAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: ACTION_SUBJECT,
				actionSubjectId: `${ACTION_SUBJECT}CreateButton`,
				attributes: {
					...analyticsAttributes,
					conversationId,
					messageId,
				},
			});

			setAwaitingInvocationId(action.invocationId);
			try {
				const processorResult = await onCustomDataConfirm(
					action,
					action.data,
					actionResponseIndicatesSuccess,
				);
				const succeeded = processorResult === true;
				setSucceededFromResponseByInvocationId((prev) => ({
					...prev,
					[action.invocationId]: succeeded,
				}));

				sendAnalyticsEvent({
					eventType: 'track',
					action: succeeded ? 'succeeded' : 'failed',
					actionSubject: ACTION_SUBJECT,
					actionSubjectId: `${ACTION_SUBJECT}CreateRequest`,
					attributes: {
						...analyticsAttributes,
						conversationId,
						messageId,
						outcome: succeeded ? 'success' : 'error',
					},
				});
			} catch (error) {
				sendAnalyticsEvent({
					eventType: 'track',
					action: 'failed',
					actionSubject: ACTION_SUBJECT,
					actionSubjectId: `${ACTION_SUBJECT}CreateRequest`,
					attributes: {
						...analyticsAttributes,
						conversationId,
						messageId,
						outcome: 'error',
					},
				});
				throw error;
			} finally {
				setAwaitingInvocationId(null);
			}
		},
		[onCustomDataConfirm, sendAnalyticsEvent, conversationId, messageId],
	);

	if (!actions.length || !isFeatureGateEnabled) {
		return null;
	}

	const anyConfirmInFlight = awaitingInvocationId !== null;

	return (
		<Stack space="space.200">
			{actions.map((action) => {
				const actionFinishedPersisted =
					getStatus(action) === 'finished' || action.status === 'finished';
				const isSuccess =
					succeededFromResponseByInvocationId[action.invocationId] === true ||
					actionFinishedPersisted;

				return (
					<CreateRequestTypeCard
						key={action.invocationId}
						payload={action.data as JsmAtlassianProjectCreateJsmRequestTypeWithFieldsPayload}
						onNext={() => handleNextForAction(action)}
						isNextLoading={awaitingInvocationId === action.invocationId}
						isNextDisabled={anyConfirmInFlight || isSuccess}
						isSuccess={isSuccess}
					/>
				);
			})}
		</Stack>
	);
};
